'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SocialSecurityCalculator() {
  const t = useTranslations("tools.socialSecurityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'ideal-age' | 'compare-ages'>('ideal-age');

  // Ideal Age Calculator State
  const [idealAgeInputs, setIdealAgeInputs] = useState({
    birthYear: 1970,
    lifeExpectancy: 83,
    investmentReturn: 5,
    cola: 3,
  });
  const [idealAgeResults, setIdealAgeResults] = useState<any>(null);

  // Compare Ages Calculator State
  const [compareInputs, setCompareInputs] = useState({
    age1: 62,
    benefit1: 1600,
    age2: 70,
    benefit2: 2810,
    investmentReturn: 5,
    cola: 3,
  });
  const [compareResults, setCompareResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Helper Functions
  const getFRA = (birthYear: number): number => {
    if (birthYear <= 1937) return 65;
    if (birthYear <= 1942) return 65 + (birthYear - 1937) * 2 / 12;
    if (birthYear <= 1954) return 66;
    if (birthYear <= 1959) return 66 + (birthYear - 1954) * 2 / 12;
    return 67;
  };

  const getBenefitMultiplier = (claimAge: number, fra: number): number => {
    if (claimAge < 62) return 0;
    if (claimAge > 70) claimAge = 70;

    if (claimAge < fra) {
      const monthsEarly = (fra - claimAge) * 12;
      const reduction = monthsEarly <= 36 ? monthsEarly * 5/9 : 36 * 5/9 + (monthsEarly - 36) * 5/12;
      return (100 - reduction) / 100;
    } else if (claimAge > fra) {
      const yearsLate = claimAge - fra;
      return 1 + (yearsLate * 0.08);
    }
    return 1;
  };

  const calculatePresentValue = (
    monthlyBenefit: number,
    startAge: number,
    endAge: number,
    cola: number,
    discountRate: number
  ): number => {
    let pv = 0;
    const months = (endAge - startAge) * 12;
    const monthlyDiscountRate = discountRate / 12 / 100;
    const monthlyCola = cola / 12 / 100;

    for (let month = 0; month < months; month++) {
      const adjustedBenefit = monthlyBenefit * Math.pow(1 + monthlyCola, month);
      const discountFactor = Math.pow(1 + monthlyDiscountRate, month);
      pv += adjustedBenefit / discountFactor;
    }

    return pv;
  };

  // Ideal Age Calculator
  const handleIdealAgeCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { birthYear, lifeExpectancy, investmentReturn, cola } = idealAgeInputs;
    const fra = getFRA(birthYear);
    const baseBenefit = 2000;

    let bestAge = 62;
    let bestPV = 0;
    const ageData = [];

    for (let age = 62; age <= 70; age++) {
      const multiplier = getBenefitMultiplier(age, fra);
      const monthlyBenefit = baseBenefit * multiplier;
      const totalBenefits = monthlyBenefit * 12 * (lifeExpectancy - age);
      const pv = calculatePresentValue(monthlyBenefit, age, lifeExpectancy, cola, investmentReturn);

      ageData.push({
        age,
        monthlyBenefit,
        totalBenefits,
        presentValue: pv
      });

      if (pv > bestPV) {
        bestPV = pv;
        bestAge = age;
      }
    }

    const optimalData = ageData.find(d => d.age === bestAge)!;
    const fraData = ageData.find(d => d.age === Math.round(fra));

    let insight = '';
    if (bestAge === 62) {
      insight = t('insightAge62');
    } else if (bestAge === 70) {
      insight = t('insightAge70');
    } else if (bestAge === Math.round(fra)) {
      insight = t('insightFRA');
    } else {
      insight = t('insightCustomAge', { age: bestAge, lifeExpectancy, investmentReturn });
    }

    setIdealAgeResults({
      optimalAge: bestAge,
      fra: fra.toFixed(1),
      fraBenefit: fraData ? fraData.monthlyBenefit : baseBenefit,
      lifetimeBenefits: optimalData.totalBenefits,
      presentValue: optimalData.presentValue,
      ageData,
      insight
    });
  };

  // Compare Ages Calculator
  const handleCompareCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { age1, benefit1, age2, benefit2, investmentReturn, cola } = compareInputs;
    const lifeExpectancy = 90;

    const pv1 = calculatePresentValue(benefit1, age1, lifeExpectancy, cola, investmentReturn);
    const pv2 = calculatePresentValue(benefit2, age2, lifeExpectancy, cola, investmentReturn);

    const difference = Math.abs(pv2 - pv1);
    const winner = pv2 > pv1 ? 2 : 1;

    let breakEvenAge = null;
    for (let age = Math.max(age1, age2); age <= lifeExpectancy; age++) {
      const pv1AtAge = calculatePresentValue(benefit1, age1, age, cola, investmentReturn);
      const pv2AtAge = calculatePresentValue(benefit2, age2, age, cola, investmentReturn);

      if (age2 > age1 && pv2AtAge >= pv1AtAge) {
        breakEvenAge = age;
        break;
      } else if (age1 > age2 && pv1AtAge >= pv2AtAge) {
        breakEvenAge = age;
        break;
      }
    }

    const yearlyData = [];
    for (let age = Math.max(age1, age2); age <= Math.min(lifeExpectancy, 95); age += 5) {
      const cum1 = age >= age1 ? calculatePresentValue(benefit1, age1, age, cola, investmentReturn) : 0;
      const cum2 = age >= age2 ? calculatePresentValue(benefit2, age2, age, cola, investmentReturn) : 0;
      const better = cum1 > cum2 ? 'Option 1' : cum2 > cum1 ? 'Option 2' : 'Equal';

      yearlyData.push({ age, cum1, cum2, better });
    }

    const insight = breakEvenAge
      ? t('comparisonInsightWithBreakeven', { breakEvenAge, winner, difference: difference.toLocaleString('en-US', {maximumFractionDigits: 0}) })
      : t('comparisonInsightNoBreakeven', { winner, difference: difference.toLocaleString('en-US', {maximumFractionDigits: 0}) });

    setCompareResults({
      winner,
      winnerAge: winner === 1 ? age1 : age2,
      pv1,
      pv2,
      difference,
      breakEvenAge,
      yearlyData,
      insight
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    purple: '#7c3aed',
    orange: '#f59e0b',
    orangeLight: '#f97316',
    green: '#10b981',
    lightBg: '#f8fafc',
    border: '#e2e8f0',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    toolHeader: {
      textAlign: 'center' as const,
    },
    h2: {
      fontWeight: 700,
      color: colors.textPrimary,
    },
    tabsContainer: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap' as const,
    },
    tabBtn: {
      flex: 1,
      minWidth: '200px',
      background: 'white',
      border: `2px solid ${colors.border}`,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontWeight: 600,
      color: colors.textSecondary,
    },
    tabBtnActive: {
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      borderColor: colors.blue,
    },
    calculatorForm: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    },
    formSection: {},
    formSectionTitle: {
      fontWeight: 700,
      color: colors.textPrimary,
      paddingBottom: '0.75rem',
      borderBottom: `2px solid ${colors.border}`,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
    },
    formGroup: {},
    label: {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      border: `2px solid ${colors.border}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    inputSuffix: {
      position: 'relative' as const,
    },
    suffixText: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontSize: '0.9rem',
      pointerEvents: 'none' as const,
    },
    calculateBtn: {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    results: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    },
    resultHighlight: {
      background: `linear-gradient(135deg, ${colors.orange} 0%, ${colors.orangeLight} 100%)`,
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
    },
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
    },
    resultItem: {
      background: colors.lightBg,
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.blue}`,
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: colors.textSecondary,
      fontWeight: 600,
    },
    resultValue: {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 700,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    th: {
      background: colors.lightBg,
      fontWeight: 700,
      color: colors.textPrimary,
      textAlign: 'left' as const,
      borderBottom: `1px solid ${colors.border}`,
    },
    td: {
      textAlign: 'left' as const,
      borderBottom: `1px solid ${colors.border}`,
    },
    winnerBadge: {
      display: 'inline-block',
      background: colors.green,
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '1rem',
      fontSize: '0.75rem',
      fontWeight: 700,
      marginLeft: '0.5rem',
    },
    infoBox: {
      background: '#eff6ff',
      borderLeft: `4px solid ${colors.blue}`,
      borderRadius: '0.5rem',
    },
    infoText: {
      color: '#1e40af',
      fontSize: '0.9rem',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    list: {
      lineHeight: 1.8,
      color: colors.textSecondary,
    },
    listItem: {
      lineHeight: 1.8,
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem', marginBottom: '1rem' },
    { fontSize: '2.5rem', marginBottom: '1.25rem' },
    { fontSize: '3rem', marginBottom: '1.5rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem', marginBottom: '2rem' },
    { padding: '2rem 1.5rem', marginBottom: '2.5rem' },
    { padding: '3rem 2rem', marginBottom: '3rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const tabBtnStyle = getResponsiveStyle(
    baseStyles.tabBtn,
    { padding: '0.875rem 1rem', fontSize: '0.95rem', minWidth: '100%' },
    { padding: '1rem 1.25rem', fontSize: '1rem', minWidth: '200px' },
    { padding: '1rem 1.5rem', fontSize: '1rem', minWidth: '200px' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    baseStyles.calculatorForm,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2rem', marginBottom: '2rem' }
  );

  const formSectionStyle = getResponsiveStyle(
    baseStyles.formSection,
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const formSectionTitleStyle = getResponsiveStyle(
    baseStyles.formSectionTitle,
    { fontSize: '1.125rem', marginBottom: '1rem' },
    { fontSize: '1.25rem', marginBottom: '1.25rem' },
    { fontSize: '1.25rem', marginBottom: '1.25rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    baseStyles.formGroup,
    { marginBottom: '1rem' },
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    baseStyles.label,
    { marginBottom: '0.5rem' },
    { marginBottom: '0.5rem' },
    { marginBottom: '0.5rem' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.25rem' },
    { padding: '0.75rem 1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    baseStyles.calculateBtn,
    { padding: '0.875rem 1.5rem', fontSize: '1rem', marginTop: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', marginTop: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem', marginTop: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    baseStyles.results,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    baseStyles.resultHighlight,
    { padding: '1.25rem', marginBottom: '1.25rem' },
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '1.5rem', marginBottom: '1.5rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    baseStyles.resultItem,
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    baseStyles.resultValue,
    { fontSize: '1.125rem', marginTop: '0.25rem' },
    { fontSize: '1.25rem', marginTop: '0.25rem' },
    { fontSize: '1.25rem', marginTop: '0.25rem' }
  );

  const thStyle = getResponsiveStyle(
    baseStyles.th,
    { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const tdStyle = getResponsiveStyle(
    baseStyles.td,
    { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    baseStyles.infoBox,
    { padding: '1rem', marginTop: '1.25rem' },
    { padding: '1rem', marginTop: '1.5rem' },
    { padding: '1rem', marginTop: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem', marginBottom: '1rem' },
    { fontSize: '1.125rem', marginBottom: '1.25rem' },
    { fontSize: '1.125rem', marginBottom: '1.25rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={baseStyles.toolHeader}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={{ ...summaryTextStyle, marginBottom: screenSize === 'mobile' ? '1.5rem' : '2rem' }}>
              {t("toolSubtitle")}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: screenSize === 'mobile' ? '1.5rem' : '2rem', flexWrap: 'wrap' }}>
            <button
              style={{
                ...tabBtnStyle,
                ...(activeTab === 'ideal-age' ? baseStyles.tabBtnActive : {})
              }}
              onClick={() => setActiveTab('ideal-age')}
              onMouseEnter={(e) => {
                if (activeTab !== 'ideal-age') {
                  e.currentTarget.style.borderColor = colors.blue;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'ideal-age') {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {t("tabIdealAge")}
            </button>
            <button
              style={{
                ...tabBtnStyle,
                ...(activeTab === 'compare-ages' ? baseStyles.tabBtnActive : {})
              }}
              onClick={() => setActiveTab('compare-ages')}
              onMouseEnter={(e) => {
                if (activeTab !== 'compare-ages') {
                  e.currentTarget.style.borderColor = colors.blue;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'compare-ages') {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {t("tabCompareAges")}
            </button>
          </div>

          {/* Ideal Age Tab */}
          {activeTab === 'ideal-age' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleIdealAgeCalculate}>
                <div style={formSectionStyle}>
                  <h3 style={formSectionTitleStyle}>{t("personalInfoTitle")}</h3>
                  <div style={baseStyles.formGrid}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="birth-year">{t("birthYearLabel")}</label>
                      <input
                        type="number"
                        id="birth-year"
                        min="1943"
                        max="2010"
                        value={idealAgeInputs.birthYear}
                        onChange={(e) => setIdealAgeInputs({...idealAgeInputs, birthYear: parseInt(e.target.value)})}
                        style={inputStyle}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = colors.blue;
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="life-expectancy">{t("lifeExpectancyLabel")}</label>
                      <input
                        type="number"
                        id="life-expectancy"
                        min="62"
                        max="120"
                        value={idealAgeInputs.lifeExpectancy}
                        onChange={(e) => setIdealAgeInputs({...idealAgeInputs, lifeExpectancy: parseInt(e.target.value)})}
                        style={inputStyle}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = colors.blue;
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <h3 style={formSectionTitleStyle}>{t("financialAssumptionsTitle")}</h3>
                  <div style={baseStyles.formGrid}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="investment-return">{t("investmentReturnLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="investment-return"
                          step="0.1"
                          min="0"
                          max="20"
                          value={idealAgeInputs.investmentReturn}
                          onChange={(e) => setIdealAgeInputs({...idealAgeInputs, investmentReturn: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("percentPerYear")}</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="cola">{t("colaLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="cola"
                          step="0.1"
                          min="0"
                          max="10"
                          value={idealAgeInputs.cola}
                          onChange={(e) => setIdealAgeInputs({...idealAgeInputs, cola: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("percentPerYear")}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  style={calculateBtnStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {t("calculateIdealAge")}
                </button>
              </form>

              {idealAgeResults && (
                <div style={resultsStyle}>
                  <h3 style={{...h2Style, textAlign: 'center'}}>{t("recommendedStrategyTitle")}</h3>

                  <div style={resultHighlightStyle}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.95, marginBottom: '0.5rem' }}>
                      {t("optimalAgeLabel")}
                    </div>
                    <div style={{ fontSize: screenSize === 'mobile' ? '1.75rem' : '2rem', fontWeight: 700 }}>
                      {idealAgeResults.optimalAge} {t("years")}
                    </div>
                  </div>

                  <div style={baseStyles.resultGrid}>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("fraLabel")}</span>
                      <span style={resultValueStyle}>{idealAgeResults.fra} {t("years")}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("fraBenefitLabel")}</span>
                      <span style={resultValueStyle}>
                        ${idealAgeResults.fraBenefit.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("lifetimeBenefitsLabel")}</span>
                      <span style={resultValueStyle}>
                        ${idealAgeResults.lifetimeBenefits.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("presentValueLabel")}</span>
                      <span style={resultValueStyle}>
                        ${idealAgeResults.presentValue.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                  </div>

                  <table style={{...baseStyles.table, marginTop: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("claimingAgeColumn")}</th>
                        <th style={thStyle}>{t("monthlyBenefitColumn")}</th>
                        <th style={thStyle}>{t("totalBenefitsColumn")}</th>
                        <th style={thStyle}>{t("presentValueColumn")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {idealAgeResults.ageData.map((data: any) => (
                        <tr key={data.age} style={{ background: 'transparent', transition: 'background 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = colors.lightBg}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={tdStyle}>
                            {data.age}
                            {data.age === idealAgeResults.optimalAge && (
                              <span style={baseStyles.winnerBadge}>{t("bestBadge")}</span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            ${data.monthlyBenefit.toLocaleString('en-US', {maximumFractionDigits: 0})}
                          </td>
                          <td style={tdStyle}>
                            ${data.totalBenefits.toLocaleString('en-US', {maximumFractionDigits: 0})}
                          </td>
                          <td style={tdStyle}>
                            ${data.presentValue.toLocaleString('en-US', {maximumFractionDigits: 0})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={infoBoxStyle}>
                    <p style={{...baseStyles.infoText, margin: '0.5rem 0'}}>
                      <strong>{t("keyInsightsLabel")}</strong>
                    </p>
                    <p style={{...baseStyles.infoText, margin: '0.5rem 0'}}>
                      {idealAgeResults.insight}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compare Ages Tab */}
          {activeTab === 'compare-ages' && (
            <div>
              <form style={calculatorFormStyle} onSubmit={handleCompareCalculate}>
                <div style={formSectionStyle}>
                  <h3 style={formSectionTitleStyle}>{t("option1Title")}</h3>
                  <div style={baseStyles.formGrid}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="age1">{t("retirementAgeLabel")}</label>
                      <input
                        type="number"
                        id="age1"
                        min="62"
                        max="70"
                        value={compareInputs.age1}
                        onChange={(e) => setCompareInputs({...compareInputs, age1: parseInt(e.target.value)})}
                        style={inputStyle}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = colors.blue;
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="benefit1">{t("monthlyBenefitLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="benefit1"
                          min="0"
                          step="10"
                          value={compareInputs.benefit1}
                          onChange={(e) => setCompareInputs({...compareInputs, benefit1: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("dollarPerMonth")}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <h3 style={formSectionTitleStyle}>{t("option2Title")}</h3>
                  <div style={baseStyles.formGrid}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="age2">{t("retirementAgeLabel")}</label>
                      <input
                        type="number"
                        id="age2"
                        min="62"
                        max="70"
                        value={compareInputs.age2}
                        onChange={(e) => setCompareInputs({...compareInputs, age2: parseInt(e.target.value)})}
                        style={inputStyle}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = colors.blue;
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        required
                      />
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="benefit2">{t("monthlyBenefitLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="benefit2"
                          min="0"
                          step="10"
                          value={compareInputs.benefit2}
                          onChange={(e) => setCompareInputs({...compareInputs, benefit2: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("dollarPerMonth")}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <h3 style={formSectionTitleStyle}>{t("financialAssumptionsTitle")}</h3>
                  <div style={baseStyles.formGrid}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="investment-return2">{t("investmentReturnLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="investment-return2"
                          step="0.1"
                          min="0"
                          max="20"
                          value={compareInputs.investmentReturn}
                          onChange={(e) => setCompareInputs({...compareInputs, investmentReturn: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("percentPerYear")}</span>
                      </div>
                    </div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="cola2">{t("colaLabel")}</label>
                      <div style={baseStyles.inputSuffix}>
                        <input
                          type="number"
                          id="cola2"
                          step="0.1"
                          min="0"
                          max="10"
                          value={compareInputs.cola}
                          onChange={(e) => setCompareInputs({...compareInputs, cola: parseFloat(e.target.value)})}
                          style={{...inputStyle, paddingRight: '3.5rem'}}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.blue;
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          required
                        />
                        <span style={baseStyles.suffixText}>{t("percentPerYear")}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  style={calculateBtnStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {t("compareOptions")}
                </button>
              </form>

              {compareResults && (
                <div style={resultsStyle}>
                  <h3 style={{...h2Style, textAlign: 'center'}}>{t("comparisonResultsTitle")}</h3>

                  <div style={resultHighlightStyle}>
                    <div style={{ fontSize: '0.9rem', opacity: 0.95, marginBottom: '0.5rem' }}>
                      {t("recommendedStrategyLabel")}
                    </div>
                    <div style={{ fontSize: screenSize === 'mobile' ? '1.75rem' : '2rem', fontWeight: 700 }}>
                      {t("claimAtAge", { age: compareResults.winnerAge })}
                    </div>
                  </div>

                  <div style={baseStyles.resultGrid}>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("option1TotalLabel")}</span>
                      <span style={resultValueStyle}>
                        ${compareResults.pv1.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("option2TotalLabel")}</span>
                      <span style={resultValueStyle}>
                        ${compareResults.pv2.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("differenceLabel")}</span>
                      <span style={resultValueStyle}>
                        ${compareResults.difference.toLocaleString('en-US', {maximumFractionDigits: 0})}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={baseStyles.resultLabel}>{t("breakEvenAgeLabel")}</span>
                      <span style={resultValueStyle}>
                        {compareResults.breakEvenAge ? `${compareResults.breakEvenAge} ${t("years")}` : t("notApplicable")}
                      </span>
                    </div>
                  </div>

                  <table style={{...baseStyles.table, marginTop: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("ageColumn")}</th>
                        <th style={thStyle}>{t("option1CumulativeColumn")}</th>
                        <th style={thStyle}>{t("option2CumulativeColumn")}</th>
                        <th style={thStyle}>{t("betterChoiceColumn")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compareResults.yearlyData.map((data: any) => (
                        <tr key={data.age} style={{ background: 'transparent', transition: 'background 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = colors.lightBg}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={tdStyle}>{data.age}</td>
                          <td style={tdStyle}>
                            ${data.cum1.toLocaleString('en-US', {maximumFractionDigits: 0})}
                          </td>
                          <td style={tdStyle}>
                            ${data.cum2.toLocaleString('en-US', {maximumFractionDigits: 0})}
                          </td>
                          <td style={tdStyle}>
                            <strong>{data.better}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={infoBoxStyle}>
                    <p style={{...baseStyles.infoText, margin: '0.5rem 0'}}>
                      <strong>{t("analysisLabel")}</strong>
                    </p>
                    <p style={{...baseStyles.infoText, margin: '0.5rem 0'}}>
                      {compareResults.insight}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t("step1Title")}</strong> {t("step1Description")}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t("step2Title")}</strong> {t("step2Description")}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t("step3Title")}</strong> {t("step3Description")}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t("step4Title")}</strong> {t("step4Description")}</li>
          <li style={baseStyles.listItem}><strong style={baseStyles.strong}>{t("step5Title")}</strong> {t("step5Description")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("insight1")}</li>
          <li style={baseStyles.listItem}>{t("insight2")}</li>
          <li style={baseStyles.listItem}>{t("insight3")}</li>
          <li style={baseStyles.listItem}>{t("insight4")}</li>
          <li style={baseStyles.listItem}>{t("insight5")}</li>
          <li style={baseStyles.listItem}>{t("insight6")}</li>
        </ul>
      </section>

      {/* Detailed Information */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("fraTitle")}
        </h3>
        <p style={paragraphStyle}>{t("fraDescription")}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("fraBirth1937")}</li>
          <li style={baseStyles.listItem}>{t("fraBirth1943")}</li>
          <li style={baseStyles.listItem}>{t("fraBirth1955")}</li>
          <li style={baseStyles.listItem}>{t("fraBirth1960")}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("earlyClaimingTitle")}
        </h3>
        <p style={paragraphStyle}>{t("earlyClaimingDescription")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("delayedClaimingTitle")}
        </h3>
        <p style={paragraphStyle}>{t("delayedClaimingDescription")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("benefitCalculationTitle")}
        </h3>
        <p style={paragraphStyle}>{t("benefitCalculationDescription")}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("calculation1")}</li>
          <li style={baseStyles.listItem}>{t("calculation2")}</li>
          <li style={baseStyles.listItem}>{t("calculation3")}</li>
          <li style={baseStyles.listItem}>{t("calculation4")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq1Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq2Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq3Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq4Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq5Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("faq6Question")}
        </h3>
        <p style={paragraphStyle}>{t("faq6Answer")}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <a href="https://www.ssa.gov/benefits/calculators/" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref1")}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.ssa.gov/oact/anypia/anypia.html" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref2")}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.nerdwallet.com/investing/calculators/social-security-calculator" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref3")}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.aarp.org/social-security/benefits-calculator/" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref4")}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://smartasset.com/retirement/social-security-calculator" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref5")}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a href="https://www.usa.gov/social-security-calculators" style={baseStyles.link} target="_blank" rel="noopener noreferrer">
              {t("ref6")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}